// components/PerksComponent.js
import React, { useState, useEffect, useRef } from "react";
import { View, Text, TouchableOpacity, Modal, Image, Animated, ScrollView, Linking, Platform, StyleSheet } from "react-native";
import { Asset } from 'expo-asset';
import { Ionicons, AntDesign, MaterialIcons } from "@expo/vector-icons";
import BenefitCard from "../BenefitCard";
import NonPartnerPassCard from "../NonPartnerPassCard";
import SharedBitesComponent from "./SharedBitesComponent";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import Constants from 'expo-constants';
import * as FileSystem from 'expo-file-system';
import { colors, fontFamily } from "../../constants/theme";

const styles = StyleSheet.create({
  mt2: { marginTop: 8 },
  mt1: { marginTop: 4 },
  rootPx: { paddingHorizontal: 4 },
  px1Pb: { paddingHorizontal: 4, paddingBottom: 60 },
  speedrailzBtnP2: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
    padding: 8,
    backgroundColor: colors.yellow300,
    borderRadius: 12,
  },
  speedrailzBtnP3: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
    padding: 12,
    backgroundColor: colors.yellow300,
    borderRadius: 12,
  },
  rowCenterFlex: { flexDirection: "row", alignItems: "center", flex: 1 },
  rowCenter: { flexDirection: "row", alignItems: "center" },
  rowCenterNoGap: { flexDirection: "row", alignItems: "center" },
  ml2Flex1: { marginLeft: 8, flex: 1 },
  speedrailzTitle: { fontSize: 24, fontFamily: fontFamily.abhayaBold, color: colors.black },
  speedrailzSub: { fontSize: 14, fontFamily: fontFamily.abhaya, color: colors.gray700, marginTop: 2 },
  specialsHeaderBtnP2: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
    padding: 8,
    backgroundColor: colors.white,
  },
  specialsHeaderBtnP3: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
    padding: 12,
    backgroundColor: colors.white,
  },
  specialsTitleMl2: { fontSize: 24, fontFamily: fontFamily.abhayaBold, marginLeft: 8, color: colors.black },
  specialsTitleMl1: { fontSize: 24, fontFamily: fontFamily.abhayaBold, marginLeft: 4, color: colors.black },
  confirmedRow: { flexDirection: "row", alignItems: "center", gap: 4 },
  confirmedText: { fontSize: 16, fontFamily: fontFamily.abhayaBold, color: colors.gray600 },
  specialsPanel: {
    backgroundColor: colors.white,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    borderTopWidth: 0,
    borderLeftWidth: 2,
    borderRightWidth: 2,
    borderBottomWidth: 2,
    borderColor: colors.gray100,
  },
  tableHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray200,
  },
  tableRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  tableHeadCell: { fontFamily: fontFamily.abhayaBold, fontSize: 14, color: colors.gray600 },
  w15: { width: "15%" },
  w30: { width: "30%" },
  w55: { width: "55%" },
  pr2: { paddingRight: 8 },
  cellBold: { fontFamily: fontFamily.abhayaBold, fontSize: 16, color: colors.black },
  cell: { fontFamily: fontFamily.abhaya, fontSize: 16, color: colors.black },
  loadingSpecials: { fontSize: 16, fontFamily: fontFamily.abhaya, padding: 12, color: colors.black },
  passesHeaderBtn: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
    padding: 12,
    marginTop: 8,
    backgroundColor: colors.white,
  },
  passesRowGap: { flexDirection: "row", alignItems: "center", gap: 8 },
  passesTitle: { fontSize: 24, fontFamily: fontFamily.abhayaBold, marginLeft: 8, color: colors.black },
  p3: { padding: 12 },
  p2: { padding: 8 },
  pdfHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray200,
  },
  pdfTitle: { fontSize: 24, fontFamily: fontFamily.abhayaBold, color: colors.black },
  pdfLoadingWrap: { flex: 1, alignItems: "center", justifyContent: "center" },
  pdfLoadingText: { fontSize: 16, fontFamily: fontFamily.abhaya, color: colors.gray600 },
  infoOverlay: { flex: 1, justifyContent: "center", alignItems: "center" },
  infoBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  infoCard: {
    width: "80%",
    padding: 24,
    borderRadius: 8,
    backgroundColor: colors.white,
  },
  infoCloseBtn: { position: "absolute", top: 16, right: 16, zIndex: 1 },
  infoTitle: {
    fontSize: 24,
    fontFamily: fontFamily.abhayaBold,
    textAlign: "center",
    marginBottom: 24,
    color: colors.black,
  },
  infoSections: { gap: 4 },
  infoBlock: { flexDirection: "column" },
  infoRow: { flexDirection: "row", alignItems: "center" },
  infoBold: { marginLeft: 8, fontSize: 16, fontFamily: fontFamily.abhayaBold, color: colors.black },
  infoSub: { marginLeft: 28, fontSize: 16, fontFamily: fontFamily.abhaya, color: colors.gray600, marginTop: 2 },
});

const PartnerRestaurantUI = ({
  selectedRestaurant,
  doesUserHaveMembership,
  getUserMembership,
  handleBenefitPress,
  getNextMembershipTier,
  getCurrentMembershipTier,
  handleMissingPerk,
  isSpecialsExpanded,
  toggleSpecials,
  dailySpecials,
  getAbbreviatedDay,
  userDoc
}) => {
  const userMembership = getUserMembership(selectedRestaurant.name);
  const [isInfoPopupVisible, setIsInfoPopupVisible] = useState(false);
  const [isPerksExpanded, setIsPerksExpanded] = useState(false);
  const perksRotateAnimation = useRef(new Animated.Value(0)).current;

  const togglePerks = () => {
    setIsPerksExpanded(!isPerksExpanded);
    Animated.spring(perksRotateAnimation, {
      toValue: isPerksExpanded ? 1 : 0,
      useNativeDriver: true,
    }).start();
  };

  const showInfoPopup = () => {
    setIsInfoPopupVisible(true);
  };

  // Get Discovery tier perks for non-members
  const getDiscoveryTier = () => {
    if (!selectedRestaurant?.membershipTiers) return null;
    return selectedRestaurant.membershipTiers.find(
      (tier) => tier.tier === "Discovery"
    );
  };

  // Get the appropriate tier to show (either current tier or Discovery for non-members)
  const getTierToShow = () => {
    if (doesUserHaveMembership(selectedRestaurant.name)) {
      return getCurrentMembershipTier(selectedRestaurant.name);
    }
    return getDiscoveryTier();
  };

  return (
    <View>
      <View style={styles.mt2}>
        <TouchableOpacity 
          onPress={toggleSpecials}
          style={styles.specialsHeaderBtnP2}
        >
          <View style={styles.rowCenter}>
            <Text style={styles.specialsTitleMl2}>Specials</Text>
            <View style={styles.rowCenterNoGap}>
              <MaterialIcons 
                name={isSpecialsExpanded ? "keyboard-arrow-down" : "keyboard-arrow-right"} 
                size={24} 
                color={colors.brownMuted} 
              />
              {selectedRestaurant?.isDailySpecialsConfirmed !== undefined && (
                <View style={styles.confirmedRow}>
                  <MaterialIcons 
                    name={selectedRestaurant.isDailySpecialsConfirmed ? "verified" : "warning"} 
                    size={20} 
                    color={selectedRestaurant.isDailySpecialsConfirmed ? colors.brownMuted : "#FBC02D"} 
                  />
                  <Text style={styles.confirmedText}>
                    {selectedRestaurant.isDailySpecialsConfirmed ? "- Confirmed" : "- Not Confirmed"}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </TouchableOpacity>
        {isSpecialsExpanded && (
          <View style={styles.specialsPanel}>
            <View style={{ height: 1, backgroundColor: 'rgba(156, 107, 66, 0.5)' }} />
            {dailySpecials ? (
              <View>
                {/* Table Header */}
                <View style={[styles.tableHeaderRow, { backgroundColor: 'rgba(156, 107, 66, 0.1)' }]}>
                  <View style={{ width: '15%' }}>
                    <Text style={styles.tableHeadCell}>Day</Text>
                  </View>
                  <View style={{ width: '30%' }}>
                    <Text style={styles.tableHeadCell}>Time</Text>
                  </View>
                  <View style={{ width: '55%' }}>
                    <Text style={styles.tableHeadCell}>Special</Text>
                  </View>
                </View>
                {/* Table Rows */}
                {dailySpecials.map((special, index) => (
                  <View 
                    key={special.dayOfWeek} 
                    style={[
                      styles.tableRow,
                      { 
                        backgroundColor: index % 2 === 0 ? colors.white : 'rgba(156, 107, 66, 0.1)'
                      }
                    ]}
                  >
                    <View style={[styles.w15, styles.pr2]}>
                      <Text style={styles.cellBold}>
                        {getAbbreviatedDay(special.dayOfWeek)}
                      </Text>
                    </View>
                    <View style={[styles.w30, styles.pr2]}>
                      <Text style={styles.cell} numberOfLines={2}>
                        {special.whenHH}
                      </Text>
                    </View>
                    <View style={styles.w55}>
                      <Text style={styles.cell} numberOfLines={3}>
                        {special.description}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            ) : (
              <Text style={styles.loadingSpecials}>Loading specials...</Text>
            )}
          </View>
        )}
      </View>

      {/* Shared Bites Component */}
      <SharedBitesComponent 
        selectedRestaurant={selectedRestaurant}
        userDoc={userDoc}
      />

      <View style={styles.px1Pb}>
      {/*   <TouchableOpacity 
          onPress={togglePerks}
          legacyCls="flex flex-row items-center w-full justify-between p-3 rounded-t-xl"
          style={{ backgroundColor: 'rgba(156, 107, 66, 0.1)' }}
        >
          <View legacyCls="flex flex-row items-center gap-x-2">
            <Text legacyCls="text-2xl font-abhayaBold ml-2">Passes</Text>
            <Ionicons
              onPress={(e) => {
                e.stopPropagation();
                showInfoPopup();
              }}
              name="information-circle"
              size={24}
              color="black"
            />
            <View>
              <MaterialIcons 
                name={isPerksExpanded ? "keyboard-arrow-down" : "keyboard-arrow-right"} 
                size={24} 
                color="black" 
              />
            </View>
          </View>
          <TouchableOpacity
            onPress={(e) => {
              e.stopPropagation();
              handleMissingPerk();
            }}
            legacyCls="bg-gray-200 rounded-full py-1.5 px-3 flex-row items-center gap-x-1"
          >
            <AntDesign name="questioncircle" size={16} color="#713F11" />
            <Text legacyCls="text-yellow-900 text-sm font-abhayaBold">
              Reclaim
            </Text>
          </TouchableOpacity>
        </TouchableOpacity>

        {isPerksExpanded && (
          <View legacyCls="bg-white p-3 rounded-b-xl border-gray-100 border-t-0 border-2">
            <View legacyCls="h-28 mt-2 border-dashed border-2 border-yellow-900 rounded-xl">
              {doesUserHaveMembership(selectedRestaurant.name) &&
              userMembership.benefits.length > 0 ? (
                <View legacyCls="flex flex-row h-full w-full p-1 items-center">
                  {userMembership.benefits.map((benefit, index) => (
                    <TouchableOpacity
                      key={benefit?._id}
                      onPress={() => handleBenefitPress(benefit)}
                      legacyCls="w-1/3"
                    >
                      <BenefitCard benefit={benefit} />
                    </TouchableOpacity>
                  ))}
                </View>
              ) : (
                <View legacyCls="flex flex-col items-center justify-center w-full h-full">
                  <AntDesign name="gift" size={32} color="#5E3D2D" />
                  <Text legacyCls="text-lg font-abhaya text-brown mt-1">
                    Check in or get sent perks
                  </Text>
                </View>
              )}
            </View>
            <Text legacyCls="text-2xl font-abhayaBold mt-6">Earnable Perks</Text>
            <View legacyCls="flex flex-row flex-wrap rounded-xl w-full gap-1">
              {getTierToShow()?.perks?.map(
                (perk, index) => (
                  <View
                    legacyCls="w-[145px] min-h-[60px] flex-col items-center p-2 border-yellow-900 border rounded-md justify-center"
                    key={index}
                  >
                    <Text legacyCls="text-sm font-abhaya text-center flex-wrap">{perk}</Text>
                  </View>
                ),
              )}
            </View>
            {doesUserHaveMembership(selectedRestaurant.name) && (
              <>
                <Text legacyCls="text-2xl font-abhayaBold mt-6">Next Tier Perks</Text>
                <View legacyCls="flex flex-row flex-wrap rounded-xl w-full gap-1 pb-8">
                  {getNextMembershipTier(selectedRestaurant.name)?.perks?.map(
                    (perk, index) => (
                      <View
                        legacyCls="w-[145px] min-h-[60px] flex-col items-center p-2 border-yellow-900 border rounded-md justify-center"
                        key={index}
                      >
                        <Text legacyCls="text-sm font-abhaya text-center flex-wrap">{perk}</Text>
                      </View>
                    ),
                  )}
                </View>
              </>
            )}
          </View>
        )} */}

        {/* Info Popup */}
        <Modal
          animationType="fade"
          transparent={true}
          visible={isInfoPopupVisible}
          onRequestClose={() => {
            setIsInfoPopupVisible(false);
          }}
        >
          <View style={styles.infoOverlay}>
            <View style={styles.infoBackdrop} />
            <View style={styles.infoCard}>
              <TouchableOpacity
                onPress={() => setIsInfoPopupVisible(false)}
                style={styles.infoCloseBtn}
              >
                <Ionicons name="close" size={24} color="black" />
              </TouchableOpacity>
              <Text style={styles.infoTitle}>
                How Disgo Works
              </Text>
              <View style={styles.infoSections}>
                <View style={styles.infoBlock}>
                  <View style={styles.infoRow}>
                    <AntDesign name="checkcircle" size={20} color="black" />
                    <Text style={styles.infoBold}>
                      Find a spot with a deal.
                    </Text>
                  </View>
                  <Text style={styles.infoSub}>
                    Disgo spots offer long-term loyalty perks. Other venues show happy hours and specials.
                  </Text>
                </View>
                <View style={styles.infoBlock}>
                  <View style={styles.infoRow}>
                    <AntDesign name="checkcircle" size={20} color="black" />
                    <Text style={styles.infoBold}>
                      Tap the check-in button.
                    </Text>
                  </View>
                  <Text style={styles.infoSub}>
                    No tag needed. Just be at the venue and press to earn points.
                  </Text>
                </View>
                <View style={styles.infoBlock}>
                  <View style={styles.infoRow}>
                    <AntDesign name="checkcircle" size={20} color="black" />
                    <Text style={styles.infoBold}>
                      Earn +1 point.
                    </Text>
                  </View>
                  <Text style={styles.infoSub}>
                    Check in to unlock rewards and climb tiers for Disgo partners only.
                  </Text>
                </View>
                <View style={styles.infoBlock}>
                  <View style={styles.infoRow}>
                    <AntDesign name="checkcircle" size={20} color="black" />
                    <Text style={styles.infoBold}>
                      Share the perk for +3 additional points.
                    </Text>
                  </View>
                  <Text style={styles.infoSub}>
                    Send it to a friend to earn more and spread the love.
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </Modal>

      </View>
    </View>
  );
};

const NonPartnerRestaurantUI = ({
  selectedRestaurant,
  isSpecialsExpanded,
  toggleSpecials,
  dailySpecials,
  getAbbreviatedDay,
  userDoc
}) => {
  const [isPassesExpanded, setIsPassesExpanded] = useState(false);
  const passesRotateAnimation = useRef(new Animated.Value(0)).current;

  const togglePasses = () => {
    setIsPassesExpanded(!isPassesExpanded);
    Animated.spring(passesRotateAnimation, {
      toValue: isPassesExpanded ? 1 : 0,
      useNativeDriver: true,
    }).start();
  };

  return (
    <View style={styles.rootPx}>
        <TouchableOpacity 
          onPress={toggleSpecials}
          style={styles.specialsHeaderBtnP3}
        >
          <View style={styles.rowCenter}>
            <Text style={styles.specialsTitleMl1}>Specials</Text>
            <View style={styles.rowCenterNoGap}>
              <MaterialIcons 
                name={isSpecialsExpanded ? "keyboard-arrow-down" : "keyboard-arrow-right"} 
                size={24} 
                color={colors.brownMuted} 
              />
              {selectedRestaurant?.isDailySpecialsConfirmed !== undefined && (
                <View style={styles.confirmedRow}>
                  <MaterialIcons 
                    name={selectedRestaurant.isDailySpecialsConfirmed ? "verified" : "warning"} 
                    size={20} 
                    color={selectedRestaurant.isDailySpecialsConfirmed ? colors.brownMuted : "#FBC02D"} 
                  />
                  <Text style={styles.confirmedText}>
                    {selectedRestaurant.isDailySpecialsConfirmed ? "- Confirmed" : "- Not Confirmed"}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </TouchableOpacity>
        {isSpecialsExpanded && (
          <View style={styles.specialsPanel}>
            <View style={{ height: 1, backgroundColor: 'rgba(156, 107, 66, 0.5)' }} />
            {dailySpecials ? (
              <View>
                {/* Table Header */}
                <View style={[styles.tableHeaderRow, { backgroundColor: 'rgba(156, 107, 66, 0.1)' }]}>
                  <View style={{ width: '15%' }}>
                    <Text style={styles.tableHeadCell}>Day</Text>
                  </View>
                  <View style={{ width: '30%' }}>
                    <Text style={styles.tableHeadCell}>Time</Text>
                  </View>
                  <View style={{ width: '55%' }}>
                    <Text style={styles.tableHeadCell}>Special</Text>
                  </View>
                </View>
                {/* Table Rows */}
                {dailySpecials.map((special, index) => (
                  <View 
                    key={special.dayOfWeek} 
                    style={[
                      styles.tableRow,
                      { backgroundColor: index % 2 === 0 ? colors.white : 'rgba(156, 107, 66, 0.1)' }
                    ]}
                  >
                    <View style={[styles.w15, styles.pr2]}>
                      <Text style={styles.cellBold}>
                        {getAbbreviatedDay(special.dayOfWeek)}
                      </Text>
                    </View>
                    <View style={[styles.w30, styles.pr2]}>
                      <Text style={styles.cell} numberOfLines={2}>
                        {special.whenHH}
                      </Text>
                    </View>
                    <View style={styles.w55}>
                      <Text style={styles.cell} numberOfLines={3}>
                        {special.description}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            ) : (
              <Text style={styles.loadingSpecials}>Loading specials...</Text>
            )}
          </View>
        )}

        {/* Shared Bites Component */}
        <View style={styles.mt1}>
          <SharedBitesComponent 
            selectedRestaurant={selectedRestaurant}
            userDoc={userDoc}
          />
        </View>

        {/* Passes Section */}
        <TouchableOpacity 
          onPress={togglePasses}
          style={styles.passesHeaderBtn}
        >
          <View style={styles.passesRowGap}>
            <Text style={styles.passesTitle}>Passes</Text>
            <View>
              <MaterialIcons 
                name={isPassesExpanded ? "keyboard-arrow-down" : "keyboard-arrow-right"} 
                size={24} 
                color="black" 
              />
            </View>
          </View>
        </TouchableOpacity>

        {isPassesExpanded && (
          <View style={styles.specialsPanel}>
            <View style={{ height: 1, backgroundColor: 'rgba(156, 107, 66, 0.5)' }} />
            <View style={styles.p3}>
              <NonPartnerPassCard restaurantName={selectedRestaurant?.name} />
            </View>
          </View>
        )}

    </View>
  );
};

function PerksComponent({
  selectedRestaurant,
  doesUserHaveMembership,
  getUserMembership,
  handleBenefitPress,
  getNextMembershipTier,
  getCurrentMembershipTier,
  handleMissingPerk,
  userDoc,
}) {
  const [isInfoPopupVisible, setIsInfoPopupVisible] = useState(false);
  const [isPerksExpanded, setIsPerksExpanded] = useState(false);
  const [isSpecialsExpanded, setIsSpecialsExpanded] = useState(false);
  const perksRotateAnimation = useRef(new Animated.Value(0)).current;
  const specialsRotateAnimation = useRef(new Animated.Value(0)).current;

  // Query daily specials when restaurant is selected
  const dailySpecials = useQuery(api.getDailySpecials.getDailySpecialsByRestaurant, 
    selectedRestaurant?._id ? {
      restaurantId: selectedRestaurant._id
    } : "skip"
  );


  const getAbbreviatedDay = (day) => {
    const abbreviations = {
      'Monday': 'Mon',
      'Tuesday': 'Tue',
      'Wednesday': 'Wed',
      'Thursday': 'Thu',
      'Friday': 'Fri',
      'Saturday': 'Sat',
      'Sunday': 'Sun'
    };
    return abbreviations[day] || day;
  };

  const togglePerks = () => {
    setIsPerksExpanded(!isPerksExpanded);
    Animated.spring(perksRotateAnimation, {
      toValue: isPerksExpanded ? 1 : 0,
      useNativeDriver: true,
    }).start();
  };

  const toggleSpecials = () => {
    setIsSpecialsExpanded(!isSpecialsExpanded);
    Animated.spring(specialsRotateAnimation, {
      toValue: isSpecialsExpanded ? 1 : 0,
      useNativeDriver: true,
    }).start();
  };

  return selectedRestaurant?.isPartner ? (
    <PartnerRestaurantUI
      selectedRestaurant={selectedRestaurant}
      doesUserHaveMembership={doesUserHaveMembership}
      getUserMembership={getUserMembership}
      handleBenefitPress={handleBenefitPress}
      getNextMembershipTier={getNextMembershipTier}
      getCurrentMembershipTier={getCurrentMembershipTier}
      handleMissingPerk={handleMissingPerk}
      isSpecialsExpanded={isSpecialsExpanded}
      toggleSpecials={toggleSpecials}
      dailySpecials={dailySpecials}
      getAbbreviatedDay={getAbbreviatedDay}
      userDoc={userDoc}
    />
  ) : (
    <NonPartnerRestaurantUI
      selectedRestaurant={selectedRestaurant}
      isSpecialsExpanded={isSpecialsExpanded}
      toggleSpecials={toggleSpecials}
      dailySpecials={dailySpecials}
      getAbbreviatedDay={getAbbreviatedDay}
      userDoc={userDoc}
    />
  );
}

export default PerksComponent;
